/*
 * Provider accounts (Expo EAS, Bitrise): pure model behind the connect / edit / replace-token forms, the cards and the
 * table, and the row actions. No React and nothing that touches `window`.
 */
import type { ProviderKind } from '@hangar/shared';
import type { ProviderAccountCreateInput, ProviderAccountPatchInput } from '@/api/endpoints';
import type { ProviderAccount, ProviderAccountTestResult } from '@/api/schemas';
import type { RowAction } from '@/components/RowActions';
import { TOKEN_REQUIRED, TOKEN_REQUIRED_ON_REPLACE, tokenFieldError } from './credentialModel';

/** The "managed by" tag of an account (display only): who owns the Expo account / Bitrise workspace. */
export interface ManagedByLook {
  /** Short, for table rows. */
  short: string;
  label: string;
  title: string;
}

export function managedByLook(account: Pick<ProviderAccount, 'managedBy'>): ManagedByLook {
  return account.managedBy === 'client'
    ? { short: 'Client', label: 'Client-managed', title: 'The client owns this provider account and brought it to Hangar' }
    : { short: 'Malible', label: 'Managed by Malible', title: 'Malible owns this Expo account / Bitrise workspace and manages it for the client' };
}

/** A Malible platform operator (`me.user.platformRole`): the only role that adds or changes provider accounts. */
export const isPlatformOperator = (me: { user?: { platformRole?: string | null } | null } | undefined): boolean => me?.user?.platformRole === 'operator';

/** What a client admin reads where an operator has the connect buttons (SPEC decisions 19 and 30: Hangar, never Malible). */
export const PROVIDED_BY_HANGAR = 'Your Hangar administrators set up your build service when your organization is created.';

export const SLOTS_MIN = 1;
export const SLOTS_MAX = 100;

/** The text that differs between the two providers, so the forms do not branch on the kind. */
export interface ConnectCopy {
  /** "EAS" / "Bitrise": where "Test connection" checks the token. */
  provider: string;
  title: string;
  intro: string;
  namePlaceholder: string;
  externalLabel: string;
  externalHint: string;
  externalPlaceholder: string;
  externalRequired: string;
  tokenLabel: string;
  /** Where the provider's token comes from (shown under the token field). */
  tokenHelp: string;
  slotsHint: string;
  credentialKind: 'eas_access_token' | 'bitrise_api_token';
}

export const CONNECT_COPY: Record<ProviderKind, ConnectCopy> = {
  eas: {
    provider: 'EAS',
    title: 'Connect Expo account',
    intro: "Hangar follows your EAS builds with this account's token.",
    namePlaceholder: 'EAS · storeai',
    externalLabel: 'Expo account',
    externalHint: 'The account or organization name exactly as on expo.dev',
    externalPlaceholder: 'storeai',
    externalRequired: 'The Expo account name is required (as on expo.dev)',
    tokenLabel: 'Robot token',
    tokenHelp: 'expo.dev → the organization → Settings → Access tokens → a robot user with the Developer role',
    slotsHint: 'Concurrent builds your EAS plan allows (free plan: 1)',
    credentialKind: 'eas_access_token',
  },
  bitrise: {
    provider: 'Bitrise',
    title: 'Connect Bitrise account',
    intro: 'Hangar starts and follows your Bitrise builds with this token.',
    namePlaceholder: 'Bitrise · Malible',
    externalLabel: 'Bitrise workspace',
    externalHint: 'The workspace slug on bitrise.io (Workspace settings)',
    externalPlaceholder: 'malible',
    externalRequired: 'The Bitrise workspace slug is required',
    tokenLabel: 'Workspace API token',
    tokenHelp: 'bitrise.io → the workspace → Workspace settings → API tokens (a personal access token also works)',
    slotsHint: 'Concurrent builds your Bitrise plan allows',
    credentialKind: 'bitrise_api_token',
  },
};

export const CREDENTIALS_MISSING =
  'This Hangar cannot store Expo tokens yet (POST /v1/credentials is not available). Register the account with the API call in docs/getting-started.md, step 2, and give the token to the worker.';

export const nameError = (name: string): string | undefined => (name.trim() ? undefined : 'Name is required');

/** The concurrency slots as typed: a whole number from 1 to 100. */
export const slotsError = (value: string): string | undefined => (/^\d+$/.test(value) && Number(value) >= SLOTS_MIN && Number(value) <= SLOTS_MAX ? undefined : `From ${SLOTS_MIN} to ${SLOTS_MAX}`);

/* ---------- Connect ---------- */

export interface ConnectValues {
  kind: ProviderKind;
  name: string;
  externalAccount: string;
  token: string;
  concurrencyLimit: string;
}

export type ConnectField = 'name' | 'externalAccount' | 'token' | 'concurrencyLimit';
export type ConnectErrors = Record<ConnectField, string | undefined>;

/** A token already stored by an earlier attempt whose account creation failed (a taken account name, say). */
export interface StoredToken {
  id: string;
  kind: ProviderKind;
}

/** What the sheet starts with: Bitrise only when asked for and the API builds on it. */
export const initialConnectValues = (initialKind: ProviderKind | undefined, bitrise: boolean): ConnectValues => ({
  kind: initialKind === 'bitrise' && bitrise ? 'bitrise' : 'eas',
  name: '',
  externalAccount: '',
  token: '',
  concurrencyLimit: '1',
});

/** The token a retry reuses: only when it was stored for the provider that is selected now. */
export const reusableToken = (stored: StoredToken | null, kind: ProviderKind): StoredToken | null => (stored && stored.kind === kind ? stored : null);

/** Validation of the connect form; a stored token (`tokenStored`) is not asked for again. */
export function connectErrors(v: ConnectValues, tokenStored: boolean): ConnectErrors {
  return {
    name: nameError(v.name),
    externalAccount: v.externalAccount.trim() ? undefined : CONNECT_COPY[v.kind].externalRequired,
    token: tokenStored ? undefined : tokenFieldError(v.token, TOKEN_REQUIRED),
    concurrencyLimit: slotsError(v.concurrencyLimit),
  };
}

/** The `POST /provider-accounts` body. */
export const connectBody = (v: ConnectValues, credentialId: string): ProviderAccountCreateInput => ({
  kind: v.kind,
  name: v.name.trim(),
  externalAccount: v.externalAccount.trim(),
  credentialId,
  concurrencyLimit: Number(v.concurrencyLimit),
  settings: {},
});

/** What a field shows: the API's message first, then the local one once submitted (or `live`). */
export const shownError = (server: Record<string, string>, local: Partial<Record<string, string | undefined>>, field: string, show: boolean): string | undefined => server[field] || (show ? local[field] : undefined);

/* ---------- Edit ---------- */

export type EditErrors = { name: string | undefined; concurrencyLimit: string | undefined };

export const editErrors = (name: string, concurrencyLimit: string): EditErrors => ({ name: nameError(name), concurrencyLimit: slotsError(concurrencyLimit) });

/** The merge patch of an edit: only what changed (empty = nothing to save). */
export function editPatch(account: ProviderAccount, name: string, concurrencyLimit: string): ProviderAccountPatchInput {
  return {
    ...(name.trim() !== account.name ? { name: name.trim() } : {}),
    ...(Number(concurrencyLimit) !== account.concurrencyLimit ? { concurrencyLimit: Number(concurrencyLimit) } : {}),
  };
}

/** Lowering the slots never cancels a running build: the toast says the lane drains. */
export const editSavedNote = (patch: ProviderAccountPatchInput, account: ProviderAccount): string | undefined =>
  patch.concurrencyLimit !== undefined && patch.concurrencyLimit < account.concurrencyLimit ? 'Running builds finish; the lane drains to the new limit.' : undefined;

/* ---------- Replace token ---------- */

export const replaceTokenError = (token: string, attached: boolean): string | undefined => (attached ? undefined : tokenFieldError(token, TOKEN_REQUIRED_ON_REPLACE));

export interface ReplaceCopy {
  title: string;
  description: string;
}

/** With a stored credential the token is replaced in place; without one it is stored (the worker used its env). */
export const replaceCopy = (hasCredential: boolean): ReplaceCopy =>
  hasCredential
    ? { title: 'Replace token', description: 'The new token replaces the stored one in place; Hangar uses it from the next request. It is stored encrypted and never shown again.' }
    : { title: 'Store token', description: 'No token is stored for this account yet (the worker uses its environment). The one you paste is stored encrypted and never shown again.' };

/* ---------- Health, slots and the connection test ---------- */

export interface HealthLook {
  status: string;
  label: string;
}

/** Status of a provider lane for the pill: disabled, not usable yet, testing now, unreachable, degraded, not checked, connected. */
export function accountHealth(a: ProviderAccount, testing = false, usable = true): HealthLook {
  if (a.status === 'disabled') return { status: 'disabled', label: 'Disabled' };
  if (!usable) return { status: 'queued', label: 'Builds soon' };
  if (testing) return { status: 'running', label: 'Testing' };
  return checkedHealth(a);
}

function checkedHealth(a: ProviderAccount): HealthLook {
  const circuit = a.health?.circuit;
  if (circuit === 'open') return { status: 'failed', label: 'Unreachable' };
  if (a.status === 'degraded' || circuit === 'half_open') return { status: 'degraded', label: 'Degraded' };
  return a.health?.checkedAt ? { status: 'succeeded', label: 'Connected' } : { status: 'queued', label: 'Not checked' };
}

const CIRCUIT_LOOK: Record<string, { tone: string; label: string }> = {
  open: { tone: 'text-failed', label: 'circuit open' },
  half_open: { tone: 'text-awaiting', label: 'recovering' },
};
const CIRCUIT_OK = { tone: 'text-succeeded', label: 'ok' };

/** The colour and word of the circuit in a card's "Health" line. */
export const circuitLook = (circuit: string | undefined): { tone: string; label: string } => CIRCUIT_LOOK[circuit ?? ''] ?? CIRCUIT_OK;

/** A card's "Slots" line: busy when every slot is in use. */
export function slotsLook(a: Pick<ProviderAccount, 'inUse' | 'concurrencyLimit'>): { tone: string; note: string } {
  const busy = a.inUse >= a.concurrencyLimit;
  return { tone: busy ? 'text-awaiting' : 'text-succeeded', note: busy ? 'busy' : `· ${a.concurrencyLimit - a.inUse} idle` };
}

export interface TestToast {
  ok: boolean;
  title: string;
  description: string | undefined;
}

/** The first check that did not pass (a skipped one only when nothing else failed): why the test failed. */
const failedCheckMessage = (checks: ProviderAccountTestResult['checks']): string | undefined =>
  checks.find((c) => c.status !== 'passed' && c.status !== 'skipped')?.message ?? checks.find((c) => c.status !== 'passed')?.message ?? undefined;

const concurrencyNote = (r: ProviderAccountTestResult): string => (r.discoveredConcurrency ? ` · ${r.discoveredConcurrency} concurrent builds on ${r.account?.plan ?? 'the plan'}` : '');

/** The toast of "Test connection" for an account. */
export const testToast = (name: string, r: ProviderAccountTestResult): TestToast =>
  r.ok ? { ok: true, title: `${name} reachable`, description: `${r.latencyMs ?? '—'} ms${concurrencyNote(r)}` } : { ok: false, title: `${name} failed the connection test`, description: failedCheckMessage(r.checks) };

/** The toast after enabling or disabling an account. */
export const statusToast = (name: string, status: 'active' | 'disabled'): { title: string; description: string | undefined } =>
  status === 'disabled' ? { title: `${name} disabled`, description: 'Running builds finish; nothing new is dispatched to it.' } : { title: `${name} enabled`, description: undefined };

/* ---------- Row actions ---------- */

export interface ActionHandlers {
  edit: (a: ProviderAccount) => void;
  replaceToken: (a: ProviderAccount) => void;
  test: (a: ProviderAccount) => void;
  enable: (a: ProviderAccount) => void;
  disable: (a: ProviderAccount) => void;
}

/** What `useCan('admin')` and the token support decide for the menu. */
export interface ActionGate {
  hidden: boolean;
  /** Why every write is disabled (the caller is not an admin). */
  reason: string | undefined;
  /** Why "Replace token" is held back (this Hangar cannot store tokens yet). */
  tokenBlocked: string | undefined;
}

/** Edit · Replace token · Test connection · Disable/Enable; nothing for a role that never sees them. */
export function providerRowActions(a: ProviderAccount, gate: ActionGate, h: ActionHandlers): RowAction[] {
  if (gate.hidden) return [];
  const { reason } = gate;
  const disabled = a.status === 'disabled';
  return [
    { key: 'edit', label: 'Edit', icon: 'edit', testId: 'provider-account-edit', disabledReason: reason, onSelect: () => h.edit(a) },
    { key: 'token', label: a.credentialId ? 'Replace token' : 'Store token', icon: 'key', testId: 'provider-account-replace-token', disabledReason: reason ?? gate.tokenBlocked, onSelect: () => h.replaceToken(a) },
    { key: 'test', label: 'Test connection', icon: 'bolt', testId: 'provider-account-test', disabledReason: reason ?? (disabled ? 'Enable it first' : undefined), onSelect: () => h.test(a) },
    disabled
      ? { key: 'enable', label: 'Enable', icon: 'play_circle', testId: 'provider-account-enable', disabledReason: reason, onSelect: () => h.enable(a) }
      : { key: 'disable', label: 'Disable', icon: 'block', testId: 'provider-account-disable', destructive: true, disabledReason: reason, onSelect: () => h.disable(a) },
  ];
}

/** The card's own "Test connection" and "Enable" buttons replace these two entries of the menu. */
export const menuActions = (actions: RowAction[]): RowAction[] => actions.filter((x) => x.key !== 'test' && x.key !== 'enable');
